import { FIELD_NOTES_INDEX } from "./field-notes.index.js"

export type FieldNoteKind = "essay" | "sidequest"

export interface NoteHeading {
  readonly id: string
  readonly text: string
  readonly level: number
}

/** What the list and the home page need: everything but the body. */
export interface FieldNoteMeta {
  readonly slug: string
  readonly kind: FieldNoteKind
  /** Series number from the wiki index; side quests and unlisted essays carry null. */
  readonly number: number | null
  readonly title: string
  /** Human label ("June 2026"). */
  readonly date: string
  /** YYYY-MM, for ordering. */
  readonly dateSort: string
  readonly modified?: string
  readonly relatedSlugs?: ReadonlyArray<string>
  readonly projectUrl?: string
  readonly headings?: ReadonlyArray<NoteHeading>
  readonly byline: string
  readonly abstract: string
  readonly tags: ReadonlyArray<string>
  /**
   * Minutes to read the body at 230 words a minute, never under one. Written
   * into the index by scripts/build-field-notes.mjs from the body's word count,
   * so the home page can show it without downloading the bodies.
   */
  readonly readingMinutes: number
}

export interface FieldNote extends FieldNoteMeta {
  readonly bodyHtml: string
}

/** Series number descending (unnumbered last), then month descending. */
export function byNumberThenDate(a: FieldNoteMeta, b: FieldNoteMeta): number {
  if (a.number !== b.number) {
    if (a.number === null) return 1
    if (b.number === null) return -1
    return b.number - a.number
  }
  return a.dateSort < b.dateSort ? 1 : a.dateSort > b.dateSort ? -1 : 0
}

/** Month descending, ties by series number descending: what "latest" means. */
export function byRecency(a: FieldNoteMeta, b: FieldNoteMeta): number {
  if (a.dateSort !== b.dateSort) return a.dateSort < b.dateSort ? 1 : -1
  return (b.number ?? 0) - (a.number ?? 0)
}

/** The whole index, in the order the two /notes sections list it. */
export const FIELD_NOTES: ReadonlyArray<FieldNoteMeta> = [...FIELD_NOTES_INDEX].sort(
  byNumberThenDate
)

/** The long-form essays in series order, 08 down to 01, then the unnumbered one. */
export const ESSAYS: ReadonlyArray<FieldNoteMeta> = FIELD_NOTES.filter((n) => n.kind === "essay")

/** The short detours, newest first. */
export const SIDE_QUESTS: ReadonlyArray<FieldNoteMeta> = FIELD_NOTES.filter(
  (n) => n.kind === "sidequest"
).sort(byRecency)

/** The `n` most recent essays, for the home page. */
export const latestEssays = (n: number): ReadonlyArray<FieldNoteMeta> =>
  [...ESSAYS].sort(byRecency).slice(0, n)

/** The short label a list entry carries: the series number, or the kind. */
export function noteLabel(note: FieldNoteMeta): string {
  if (note.kind === "sidequest") return "Side Quest"
  return note.number === null ? "Field Note" : `No. ${String(note.number).padStart(2, "0")}`
}

/**
 * The reader's eyebrow: "Field Notes · No. 02" for a numbered essay, "Field Notes"
 * for an unnumbered one, "Side Quest" for a side quest.
 */
export function noteEyebrow(note: FieldNoteMeta): string {
  if (note.kind === "sidequest") return "Side Quest"
  return note.number === null ? "Field Notes" : `Field Notes · ${noteLabel(note)}`
}

/** The schema.org articleSection a note belongs to, and the section heading on /notes. */
export const noteSection = (note: FieldNoteMeta): string =>
  note.kind === "sidequest" ? "Side Quests" : "Field Notes"

/** "1 min read", "12 min read". */
export const readingTime = (note: FieldNoteMeta): string => `${note.readingMinutes} min read`

/**
 * Resolve one note WITH its body. The bodies module is 150 kB of HTML, so it is
 * reached only here, through a dynamic import the bundler splits into its own
 * chunk; the home route and the list never download it.
 */
export async function loadFieldNote(slug: string): Promise<FieldNote | undefined> {
  const meta = FIELD_NOTES_INDEX.find((n) => n.slug === slug)
  if (meta === undefined) return undefined
  const { FIELD_NOTE_BODIES } = await import("./field-notes.data.js")
  const bodyHtml = FIELD_NOTE_BODIES[slug]
  if (bodyHtml === undefined) return undefined
  return { ...meta, bodyHtml }
}
